import { useEffect, useRef, useState } from 'react';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { v4 as uuidv4 } from 'uuid';
import './popup-form.css';

export interface PopupFormProps {
  onClose: (closedBy: ClosedBy) => void;
};

export type ContactInfo = {
  name: string,
  email: string,
  message: string
};

export enum ClosedBy {
  None = 0,
  SendButton = 1,
  CancelButton = 2,
  EscapeKey = 3,
};

const popupId = uuidv4();

const PopupForm = (props: PopupFormProps) => {
  const appInsights = useAppInsightsContext();
  const [contactInfo, setContactInfo] = useState({});
  
  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  
  const trackEvent = useTrackEvent(appInsights, "ContactInfo", contactInfo);

  useEffect(() => {
    if (sendButtonRef.current) {
      sendButtonRef.current.disabled = !isFormValid();
    }
  });

  const isFormValid = () => {
    if (containerRef.current) {
      const elements = containerRef.current.querySelectorAll("input[required], textarea[required]");
      for (let i = 0; i < elements.length; i++) {
        let value = "";
        let element = elements[i] as HTMLElement;
        switch (elements[i].tagName) {
          case "INPUT":
            value = (element as HTMLInputElement).value;
            break;
          case "TEXTAREA":
            value = (element as HTMLTextAreaElement).value;
            break;
        }

        if (value.length === 0) {
          return false;
        }
      }
    }

    return true;
  }
  
  useEffect(() => {
    trackEvent(contactInfo);
  }, [contactInfo, trackEvent]);

  const textboxChange = (event: { target: { getAttribute: (arg0: string) => any; value: string | any[]; }; }) => {
    if (sendButtonRef.current) {
      sendButtonRef.current.disabled = !isFormValid();
    }
  };

  const onSubmit = () => {
    const submission: ContactInfo = {
      name: nameRef.current?.value ?? "",
      email: emailRef.current?.value ?? "",
      message: messageRef.current?.value ?? "",
    }
    setContactInfo(submission);
  }

  if (!contactInfo) {
    setContactInfo({
      name: nameRef.current?.value,
      email: emailRef.current?.value,
      message: messageRef.current?.value,
    });
  }

  const onContainerKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      props.onClose(ClosedBy.EscapeKey);
    }
  }

  return (
    <div id={popupId} ref={containerRef} onKeyUp={onContainerKeyUp} className="popup-form visibility-hidden">
      <div className="popup-form-label name-label">
        <label htmlFor="nameTextbox">Name</label> <span className="required-field-indicator">*</span>
      </div>

      <div className="popup-form-control name-control">
        <input
          id="nameTextbox"
          type="text"
          name="nameTextbox"
          ref={nameRef}
          required
          title="Your name"
          placeholder="Your name"
          aria-description="Your name, this field is required"
          onChange={textboxChange} />
      </div>

      <div className="popup-form-label email-label">
        <label htmlFor="emailTextbox">E-mail</label>
      </div>
      <div className="popup-form-control email-control">
        <input
          id="emailTextbox"
          type="email"
          ref={emailRef}
          name="emailTextbox"
          title="E-mail address"
          placeholder="user@domain.com"
          aria-description="Your e-mail address, this field is optional" />
      </div>

      <div className="popup-form-label message-label">
        <label htmlFor="messageTextbox">Message</label> <span className="required-field-indicator">*</span>
      </div>
      <div className="popup-form-control message-control">
        <textarea
          id="messageTextbox"
          name="messageTextbox"
          ref={messageRef}
          required
          title="Message"
          rows={3}
          placeholder="Your message"
          aria-description="Your message, this field is required"
          onChange={textboxChange} />
      </div>

      <div className="buttons popup-form-control">
        <button
          id="sendButton"
          type="button"
          ref={sendButtonRef}
          title="Send message"
          aria-description="Submit button sends your message to us"
          onClick={(event) => {onSubmit(); props.onClose(ClosedBy.SendButton);}}
          className="button send-mail">Send</button>
        <button
          id="cancelButton"
          type="button"
          title="Cancel"
          aria-description="Cancel button closes the form without submitting"
          onClick={(event) => props.onClose(ClosedBy.CancelButton)}
          className="button cancel">Cancel</button>
      </div>
    </div>
  )
}

export default PopupForm;
export function ShowPopup() {
  const popupForm = document.getElementById(popupId);
  if (popupForm) {
    popupForm.classList.remove(...["visibility-hidden"]);
  }
  const nameTextbox = document.getElementById("nameTextbox");
  if (nameTextbox) {
    nameTextbox.focus();
  }
}
export function HidePopup() {
  const popupForm = document.getElementById(popupId);
  if (popupForm) {
    popupForm.classList.add(...["visibility-hidden"]);
  }
}